import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import moment from "moment";
import { Transition } from "../../../UiComponents/Transition/Transition";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
// The Company Wallet dialogs' own look (navy header band, bordered inputs, footer), like the Brisk dialogs.
import Styles from "../../ClientCompanyWallet/ClientCompanyWallet.module.scss";
import { DialogFrame } from "../../Policy/BriskCertificateList/BriskV2Dialogs";
import { createRoadzenUser, listRoadzenHistory } from "../../../Service/RoadzenService";
import { INDIAN_STATES } from "../indianStates";
import { EMPTY_USER, RESULT_LABEL, RESULT_TONE, createUserSchema, historyTitle, userPayload } from "./roadzenUsersUtils";
import { zenProblem } from "../RoadzenWallet/roadzenWalletUtils";

const buttonSx = { textTransform: "none", borderRadius: "6px" };
const TONE_COLOURS = { green: "#12B76A", red: "#F04438", amber: "#F79009", navy: "#1E3A5F", grey: "#98A2B3" };
const ACTION_ICONS = {
  CREATE_USER: PersonAddAltRoundedIcon,
  ACTIVATE_USER: CheckRoundedIcon,
  DEACTIVATE_USER: BlockRoundedIcon,
  TEST_CREDIT: AddCardRoundedIcon,
  TOPUP_LINK: LinkRoundedIcon,
};

/** One labelled Formik field with the Company Wallet input look; `as="select"` for the State list. */
const FormField = ({ name, label, required, errors, touched, children, ...props }) => (
  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
    <Typography component="label" htmlFor={`roadzen-${name}`} className={Styles.FieldLabel}>
      {label}
      {required && <span style={{ color: "#B42318" }}> *</span>}
    </Typography>
    <Field id={`roadzen-${name}`} name={name} className={Styles.InputFiled} {...props}>
      {children}
    </Field>
    {touched[name] && errors[name] && <Typography className={Styles.FieldError}>{errors[name]}</Typography>}
  </Box>
);

const twoColumns = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 };

/**
 * Create User (super admin). RoadZen makes every new user active; the server switches the new one off at once when
 * another user is already active, so the one-active rule holds. `onDone` is called after every answer (the list is
 * read again), and the dialog closes only when the user was created.
 */
export const CreateUserDialog = ({ open, activeUser, onClose, onDone }) => {
  const [problem, setProblem] = useState("");
  useEffect(() => {
    if (open) setProblem("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{ sx: { maxWidth: "520px !important", width: "100%", borderRadius: "10px" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between" }} className={Styles.DialogHeader}>
        <Box>
          <Typography className={Styles.DialogTitle}>Create User</Typography>
          <Typography className={Styles.DialogSubtitle}>A new RoadZen user (seller)</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "white" }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Formik
        initialValues={EMPTY_USER}
        validationSchema={createUserSchema}
        onSubmit={(values, { setSubmitting }) => {
          setProblem("");
          createRoadzenUser(userPayload(values))
            .then((res) => {
              const created = !res.error || res.code === "CREATED_BUT_ACTIVE";
              if (res.error) ToastError(res.message);
              else ToastSuccess(res.message);
              onDone(res);
              if (created) onClose();
              else setProblem(res.message);
            })
            .finally(() => setSubmitting(false));
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form noValidate>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} className={Styles.DialogBody}>
              <Typography sx={{ fontSize: 12.5, color: "#667085" }}>
                {activeUser
                  ? `${activeUser.name} is the active RoadZen user, so the new user is kept inactive. Activate it later if it should take over.`
                  : "No RoadZen user is active, so the new user will be the active one."}
              </Typography>
              {problem && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {problem}
                </Alert>
              )}
              <FormField name="name" label="Name" required autoFocus placeholder="Seller name" errors={errors} touched={touched} />
              <Box sx={twoColumns}>
                <FormField name="mobile" label="Mobile" required inputMode="numeric" maxLength={10} placeholder="10 digit mobile" errors={errors} touched={touched} />
                <FormField name="email" label="Email" type="email" placeholder="name@example.com" errors={errors} touched={touched} />
              </Box>
              <FormField name="address" label="Address" placeholder="Door no, street, area" errors={errors} touched={touched} />
              <Box sx={twoColumns}>
                <FormField name="city" label="City" placeholder="City" errors={errors} touched={touched} />
                {/* a native list: white and as tall as the text boxes (browsers draw a select grey and shorter) */}
                <FormField name="state" label="State" as="select" style={{ backgroundColor: "#fff", height: 36.5625 }} errors={errors} touched={touched}>
                  <option value="">Choose a state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </FormField>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: "10px" }} className={Styles.DialogFooter}>
              <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                className={Styles.TopupButton}
                startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <PersonAddAltRoundedIcon sx={{ fontSize: "16px !important" }} />}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating" : "Create User"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const PAGE_SIZE = 20;

/**
 * One page's own history, newest first, in plain words (who, when, what happened, the result). Each page shows only its
 * own (owner, 2026-11-03): kind "users" = the Users page's changes; kind "wallet" = money added (for `userId` when given).
 */
const HISTORY_TEXT = {
  users: { subtitle: "Changes to the RoadZen users (create, activate, deactivate)", empty: "No user was changed from this page yet" },
  wallet: { subtitle: "Money added to the RoadZen wallet (test credit, payment links)", empty: "No money was added from this page yet" },
};
export const HistoryDialog = ({ open, onClose, kind = "users", userId = null, userName = "" }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    setError("");
    listRoadzenHistory({ page, limit: PAGE_SIZE, kind, ...(userId ? { userId } : {}) })
      .then((res) => {
        if (!alive) return;
        if (res.error) {
          setError(res.message);
          return;
        }
        setRows((prev) => (page === 1 ? res.data.rows : [...prev, ...res.data.rows]));
        setTotal(res.data.total || 0);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, page, kind, userId]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setPage(1);
    }
  }, [open]);

  if (!open) return null;
  return (
    <DialogFrame
      title="History"
      subtitle={`${(HISTORY_TEXT[kind] || HISTORY_TEXT.users).subtitle}${userName ? ` · ${userName}` : ""}`}
      onClose={onClose}
      maxWidth={760}
      footer={
        <Button variant="outlined" sx={buttonSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && !rows.length && <Typography sx={{ fontSize: 12.5, color: "#667085" }}>{(HISTORY_TEXT[kind] || HISTORY_TEXT.users).empty}</Typography>}
      <Box data-testid="roadzen-history">
        {rows.map((row) => {
          const colour = TONE_COLOURS[RESULT_TONE[row.result]] || TONE_COLOURS.grey;
          const Icon = ACTION_ICONS[row.action] || CheckRoundedIcon;
          return (
            <Box
              key={row.id}
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: "26px minmax(0, 1fr)",
                columnGap: 1.25,
                pb: 1.75,
                "&:last-of-type": { pb: 0 },
                "&:not(:last-of-type)::after": { content: '""', position: "absolute", left: "12px", top: "28px", bottom: "2px", width: "2px", backgroundColor: "#E4E7EC" },
              }}
            >
              <Box sx={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: colour, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon sx={{ fontSize: 15 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1.5, minHeight: 26 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 700, color: "#101828" }}>
                      {historyTitle(row)}
                      {row.userCode ? ` (${row.userCode})` : ""}
                    </Typography>
                    <Chip size="small" label={RESULT_LABEL[row.result] || row.result} sx={{ height: 20, fontSize: 11, fontWeight: 600, color: "#fff", backgroundColor: colour }} />
                  </Box>
                  <Typography component="div" sx={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>
                    {row.at ? moment(row.at).format("DD MMM YYYY, hh:mm A") : ""}
                    {row.byName ? ` · by ${row.byName}` : ""}
                  </Typography>
                </Box>
                <Typography component="div" sx={{ fontSize: 12.5, color: "#475467", overflowWrap: "anywhere" }}>
                  {row.message}
                </Typography>
                {/* a payment link's Zen Wallet credit comes later than its message (when RoadZen says it is paid) */}
                {row.zen && row.zen.text && !String(row.message || "").includes(row.zen.text) && (
                  <Typography
                    component="div"
                    data-testid="roadzen-history-zen"
                    sx={{ fontSize: 12.5, fontWeight: 600, mt: 0.25, overflowWrap: "anywhere", color: zenProblem(row.zen) ? "#B42318" : row.zen.state === "DONE" ? "#027A48" : "#475467" }}
                  >
                    {row.zen.text}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {loading && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} aria-label="Loading history" />}
      {!loading && rows.length < total && (
        <Button variant="outlined" sx={{ ...buttonSx, alignSelf: "center", mt: 2 }} onClick={() => setPage((p) => p + 1)}>
          Show older
        </Button>
      )}
    </DialogFrame>
  );
};
